import { useEffect, useMemo, useRef } from "react";
import { embedHtml, loadScriptOnce } from "../lib/embeds";

function transform(html) {
  if (!html || typeof window === "undefined") return html || "";
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const host = window.location.hostname;

  doc.querySelectorAll("a[href]").forEach((a) => {
    const href = a.getAttribute("href") || "";
    if (!/^https?:\/\//i.test(href)) return;
    let external = true;
    try { external = new URL(href).hostname !== host; } catch { /* keep external */ }
    if (!external) return;
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
    a.classList.add("external-link");
  });

  // In-body images sit below the hero, so defer them and give the browser their real size when the CMS stored it.
  doc.querySelectorAll("img").forEach((img) => {
    if (!img.hasAttribute("loading")) img.setAttribute("loading", "lazy");
    if (!img.hasAttribute("decoding")) img.setAttribute("decoding", "async");
    if (!img.hasAttribute("alt")) img.setAttribute("alt", "");
  });

  doc.querySelectorAll(".pwm-embed").forEach((node) => {
    const { provider, url } = node.dataset;
    const wrap = doc.createElement("div");
    wrap.innerHTML = embedHtml(provider, url);
    node.replaceWith(...wrap.childNodes);
  });

  return doc.body.innerHTML;
}

export const ArticleBody = ({ html, className = "prose-editorial", testId = "article-body" }) => {
  const ref = useRef(null);
  const processed = useMemo(() => transform(html), [html]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const timers = [];
    const retry = (fn, tries = 12) => {
      const t = setInterval(() => {
        if (fn() || --tries <= 0) clearInterval(t);
      }, 500);
      timers.push(t);
    };
    if (el.querySelector(".twitter-tweet")) {
      loadScriptOnce("https://platform.twitter.com/widgets.js").then(() =>
        retry(() => {
          if (!window.twttr?.widgets?.load) return false;
          window.twttr.widgets.load(el);
          return true;
        })
      );
    }
    if (el.querySelector(".instagram-media")) {
      loadScriptOnce("https://www.instagram.com/embed.js").then(() =>
        retry(() => {
          if (!window.instgrm?.Embeds?.process) return false;
          window.instgrm.Embeds.process();
          return !el.querySelector("blockquote.instagram-media");
        })
      );
    }
    return () => timers.forEach(clearInterval);
  }, [processed]);

  return <div ref={ref} className={className} data-testid={testId} dangerouslySetInnerHTML={{ __html: processed }} />;
};

export default ArticleBody;
